import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Link,
    Box,
} from "@mui/material"
import React from "react"

interface ISubmitConfirmationProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    title: string
    message: string
    tableData: {
        technologyType: string
        dateOfApplication: string
        applicationId?: string
        isYourApplication?: boolean
    }[]
    showLink?: boolean
}

/**
 * SubmitConfirmation component displays a confirmation dialog for submitting an application.
 * It shows duplicate application details in a collapsible table format.
 *
 * @param {ISubmitConfirmationProps} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */
const SubmitConfirmation: React.FC<ISubmitConfirmationProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    tableData,
    showLink,
}) => (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ textAlign: "center" }}>{title}</DialogTitle>
        <DialogContent>
            <Typography variant="body1" gutterBottom>
                {message}
            </Typography>
            <Box sx={{ mt: 3 }}>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="table-content"
                        id="table-header"
                    >
                        <Typography fontWeight="500">
                            Review Recently Submitted Applications
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Technology Type</TableCell>
                                        <TableCell>
                                            Date of Application
                                        </TableCell>
                                        {tableData.some(
                                            (row) => row.applicationId
                                        ) && (
                                            <TableCell>
                                                Application ID
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableData.map((row, index) => (
                                        <TableRow
                                            key={
                                                (row.applicationId &&
                                                    row.technologyType &&
                                                    row.dateOfApplication) ||
                                                `row-${index}`
                                            }
                                        >
                                            <TableCell>
                                                {row.technologyType}
                                            </TableCell>

                                            <TableCell>
                                                {row.dateOfApplication}
                                            </TableCell>
                                            {row.applicationId && (
                                                <TableCell>
                                                    {row.isYourApplication &&
                                                    showLink ? (
                                                        <Link
                                                            key={
                                                                row.applicationId
                                                            }
                                                            href={`/installer-application/${row.applicationId}`}
                                                            underline="hover"
                                                            color="primary"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{
                                                                display:
                                                                    "block",
                                                                mb: 1,
                                                            }}
                                                        >
                                                            {row.applicationId}
                                                        </Link>
                                                    ) : (
                                                        row.applicationId
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} color="secondary">
                Cancel
            </Button>
            <Button onClick={onConfirm} color="primary" variant="contained">
                Confirm
            </Button>
        </DialogActions>
    </Dialog>
)

SubmitConfirmation.defaultProps = {
    showLink: false,
}

export default SubmitConfirmation
